import { ReactNode, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { DialogState } from '@components/primitives';

import { USER_QUERY_KEY } from '@queries/user';
import { isAlgunaSelfServeEnabled } from '@utils/flags/alguna';

import { UpgradeModal as LegacyUpgradeModal } from './Legacy/UpgradeModal';
import { UpgradeCheckout } from './UpgradeCheckout';
import { UpgradeDrawer } from './UpgradeDrawer';

import { monitoring } from '@/services/monitoring';

type UpgradeModalProps = {
  teamUuid: string;
  state: DialogState;
  title?: string;
  subText?: string;
  trigger?: ReactNode;
};

export function UpgradeModal({
  teamUuid,
  state,
  title,
  trigger,
  subText,
}: UpgradeModalProps) {
  const flags = useFlags();
  const algunaEnabled = isAlgunaSelfServeEnabled(flags);
  const [paid, setPaid] = useState(false);
  const [completed, setCompleted] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if ((!paid && !completed) || state.isOpen) return;

    setPaid(false);
    setCompleted(false);
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: [{ teamUuid }] }),
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ['currentUsage', teamUuid] }),
      queryClient.invalidateQueries({ queryKey: ['invoices', teamUuid] }),
    ]);
  }, [paid, completed, state.isOpen, queryClient, teamUuid]);

  useEffect(() => {
    if (algunaEnabled && state.isOpen) {
      monitoring.trackAction('checkout_opened', { teamUuid });
    }
  }, [algunaEnabled, state.isOpen, teamUuid]);

  if (!algunaEnabled) {
    return (
      <LegacyUpgradeModal
        teamUuid={teamUuid}
        state={state}
        title={title}
        subText={subText}
        trigger={trigger}
      />
    );
  }

  return (
    <UpgradeDrawer state={state} trigger={trigger} dismissible={!paid}>
      <UpgradeCheckout
        teamUuid={teamUuid}
        paid={paid}
        completed={completed}
        title={title}
        subText={subText}
        onPaid={() => setPaid(true)}
        onComplete={() => setCompleted(true)}
        onDismiss={state.close}
      />
    </UpgradeDrawer>
  );
}
